import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import { changeColorClass, formatDate, formatDateTime, formatKrw, formatPercent, toThousands } from '@/lib/format';
import { ApiException } from '@/lib/api/types';
import * as paperApi from './paperApi';
import type { PaperRoundHistory } from './paperTypes';
import { REVIEW_PAUSED_NOTE } from './reviewPause';

/**
 * AI 모의투자 회차 이력. (2026-09-03 사용자 요청)
 *
 * "회차별로 발생한 회고들을 모두 모으고는 있어? 히스토리로?
 *  그것도 관리해서 모두 볼 수 있으면 좋겠다"
 *
 * 🔴 새로 쌓기 시작한 것이 아니다. 원래 다 있던 것을 꺼내 보여준다
 *   초기화는 계좌의 시드만 되돌리고 체결·회고는 건드리지 않는다.
 *   1회차 회고도 DB 에 그대로 있었는데 볼 화면이 없었을 뿐이다.
 *
 * 🔴 회차끼리 견주는 것이 이 실험의 요점이다
 *   한 회차의 순위는 3~4주짜리 표본이라 흔들린다. 같은 계좌가 회차를 넘어
 *   꾸준한지를 봐야 규칙을 바꿀 근거가 된다. (비교 탭 도움말과 같은 이야기)
 */
export function PaperRoundsPanel() {
  const rounds = useQuery<PaperRoundHistory[], ApiException>({
    queryKey: ['trading', 'paper', 'rounds'],
    queryFn: paperApi.fetchPaperRounds,
  });

  /** 펼친 회차. 처음에는 아무것도 안 펼친다 - 목록부터 보여야 회차가 몇 개인지 보인다 */
  const [openRound, setOpenRound] = useState<number | null>(null);

  if (rounds.isLoading) {
    return (
      <Card title="회차 이력">
        <Spinner label="회차를 불러오는 중" />
      </Card>
    );
  }

  if (rounds.error) {
    return (
      <Card title="회차 이력">
        <ErrorState message={rounds.error.message} onRetry={() => void rounds.refetch()} />
      </Card>
    );
  }

  const list = rounds.data ?? [];

  if (list.length === 0) {
    return (
      <Card title="회차 이력">
        <EmptyState
          title="아직 회차가 없습니다"
          description="관리자 화면에서 기간을 정해 새 회차를 시작하면 1회차가 시작됩니다."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card title={`회차 이력 (${list.length}회차)`}>
        <p className="mb-3 text-xs text-fg-subtle">
          회차를 누르면 그 기간의 계좌별 성적 · AI 종합 분석 · 회고를 펼쳐 봅니다.
          한 회차의 순위는 3~4주짜리 표본이라 흔들립니다 — 같은 계좌가 회차를 넘어
          꾸준한지를 보는 것이 이 실험의 요점입니다.
        </p>

        <div className="flex flex-col gap-2">
          {list.map((round) => (
            <RoundRow
              key={round.roundNo}
              round={round}
              open={openRound === round.roundNo}
              onToggle={() =>
                setOpenRound((current) => (current === round.roundNo ? null : round.roundNo))
              }
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

/**
 * 회차 한 줄. 누르면 아래로 펼쳐진다.
 *
 * 🔴 상세는 펼칠 때 받는다
 *   회고 본문이 회차당 수십 건이라 목록에 다 실으면 응답이 무거워진다.
 *   서버도 목록에서는 회고를 비워 보낸다.
 */
function RoundRow({
  round,
  open,
  onToggle,
}: {
  round: PaperRoundHistory;
  open: boolean;
  onToggle: () => void;
}) {
  const detail = useQuery<PaperRoundHistory, ApiException>({
    queryKey: ['trading', 'paper', 'rounds', round.roundNo],
    queryFn: () => paperApi.fetchPaperRound(round.roundNo),
    enabled: open,
  });

  // 그 회차의 1등. 성적이 다 0이면(매매 0건) 보여주지 않는다
  const best = [...round.accounts]
    .filter((account) => account.closedCount > 0)
    .sort((a, b) => (b.returnPercent ?? 0) - (a.returnPercent ?? 0))[0];

  return (
    <div className="rounded-lg border border-line">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-surface-hover"
      >
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="font-medium text-fg">{round.roundNo}회차</span>
          {round.running ? (
            <Badge tone="success">진행 중</Badge>
          ) : (
            <Badge tone="neutral">종료</Badge>
          )}
          <span className="whitespace-nowrap text-xs text-fg-muted">
            {formatDate(round.startedAt)} ~{' '}
            {round.endedAt
              ? formatDate(round.endedAt)
              : round.plannedEndDate
                ? `${formatDate(round.plannedEndDate)} 예정`
                : '기한 없음'}
          </span>
        </span>

        <span className="flex shrink-0 items-baseline gap-2 text-xs">
          {best ? (
            <>
              <span className="text-fg-subtle">최고</span>
              <span className={cx('tabular font-medium', changeColorClass(best.returnPercent))}>
                {best.returnPercent === null ? '-' : formatPercent(best.returnPercent)}
              </span>
              <span className="text-fg-muted">{best.label}</span>
            </>
          ) : (
            <span className="text-fg-subtle">매매 없음</span>
          )}
          <span className="text-fg-subtle">{open ? '▲' : '▼'}</span>
        </span>
      </button>

      {open && (
        <div className="border-t border-line px-3 py-3">
          {detail.isLoading && <Spinner label="회차를 불러오는 중" />}
          {detail.error && (
            <ErrorState
              message={detail.error.message}
              onRetry={() => void detail.refetch()}
            />
          )}
          {detail.data && <RoundDetail round={detail.data} />}
        </div>
      )}
    </div>
  );
}

function RoundDetail({ round }: { round: PaperRoundHistory }) {
  /*
    🔴 마감 성적표가 있는 회차인가 (U-31, 2026-09-25)
      3회차 종료부터는 팔지 않고 넘긴다 - 실현 손익만으로는 성적이 아니다.
      서버가 성적표(시작 · 마감 평가액)로 채워 주고, 화면은 열 이름과 각주를 바꾼다.
  */
  const hasCard = round.accounts.some((a) => a.startEquity !== null && a.startEquity !== undefined);
  return (
    <div className="flex flex-col gap-4">
      {/* ── 계좌별 성적 ── */}
      <div>
        <h4 className="mb-1 text-xs font-medium text-fg-muted">계좌별 성적</h4>
        <Table>
          <thead>
            <tr>
              <Th>계좌</Th>
              <Th align="right">청산</Th>
              <Th align="right">{hasCard ? '회차 손익' : '실현 손익'}</Th>
              <Th align="right">수익률</Th>
              {hasCard && <Th align="right">넘긴 보유</Th>}
            </tr>
          </thead>
          <tbody>
            {round.accounts.map((account) => (
              <tr key={account.profile}>
                <Td className="font-medium">{account.label}</Td>
                <Td align="right" numeric>
                  {account.closedCount}건
                </Td>
                <Td align="right" numeric>
                  <span className={changeColorClass(account.realizedPnl)}>
                    {formatKrw(account.realizedPnl)}
                  </span>
                </Td>
                <Td align="right" numeric>
                  <span className={changeColorClass(account.returnPercent)}>
                    {account.returnPercent === null
                      ? '-'
                      : formatPercent(account.returnPercent)}
                  </span>
                </Td>
                {hasCard && (
                  <Td align="right" numeric>
                    {account.carriedCount === null ? '-' : `${account.carriedCount}건`}
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        <p className="mt-2 text-xs text-fg-subtle">
          {/*
            🔴 평가 손익이 없는 이유를 적는다. 없으면 "왜 계좌 화면과 다르지" 가 된다
          */}
          {hasCard ? (
            <>
              <strong>마감일 평가액 − 시작 평가액</strong>입니다. 3회차가 끝난 뒤로는 보유를 팔지 않고
              다음 회차로 넘기므로, 넘긴 보유의 평가 손익까지 들어 있습니다. 평가는 마감일 확정 종가로 했고,
              수익률의 분모는 계좌마다의 시작 평가액입니다.
            </>
          ) : round.running ? (
            <>
              진행 중이라 <strong>지금까지 판 것(실현 손익)만</strong> 셉니다. 들고 있는 종목의 평가 손익까지
              넣은 이번 회차 수익률은 비교 탭의 "이번 회차 수익률" 과 계좌 카드의 "이번 회차" 줄에 있습니다.
            </>
          ) : (
            <>
              <strong>실현 손익만</strong> 셉니다. 이 회차는 끝날 때 보유를 전량 청산했으므로
              평가할 것이 남지 않습니다. 시드 {formatKrw(round.seed)} 기준입니다.
            </>
          )}
        </p>
      </div>

      {/* ── 종합 분석 ── */}
      <div>
        <h4 className="mb-1 text-xs font-medium text-fg-muted">AI 종합 분석</h4>
        {round.summary ? (
          <div className="space-y-3 rounded-lg bg-surface-muted p-3 text-sm leading-relaxed text-fg-muted">
            {round.summary.split(/\n\s*\n/).map((para, i) => (
              <p key={i} className="whitespace-pre-wrap">
                {toThousands(para.trim())}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm text-fg-subtle">
            {round.running
              ? '진행 중인 회차라 아직 없습니다. 기간이 끝나면 배치가 씁니다.'
              : '아직 쓰이지 않았습니다. 하루 한 회차씩 채우므로 밀린 회차가 여럿이면 순서를 기다립니다.'}
          </p>
        )}
      </div>

      {/* ── 회고 ── */}
      <div>
        <h4 className="mb-1 text-xs font-medium text-fg-muted">
          회고 {round.reviews.length}건
        </h4>
        {round.reviews.length === 0 ? (
          <p className="text-sm text-fg-subtle">
            이 기간에 쓰인 회고가 없습니다. {REVIEW_PAUSED_NOTE}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {round.reviews.map((review, index) => (
              <li key={index} className="rounded-lg border border-line p-3">
                <div className="mb-1 flex flex-wrap items-baseline gap-2 text-xs">
                  <span className="font-medium text-fg">{review.label}</span>
                  {review.stockName && (
                    <span className="text-fg-muted">{review.stockName}</span>
                  )}
                  <span className="ml-auto whitespace-nowrap text-fg-subtle">
                    {formatDateTime(review.writtenAt)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-fg-muted">
                  {toThousands(review.content)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
